import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  addMonths,
  endOfMonth,
  format,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
} from "date-fns";
import { ChevronLeft, ChevronRight, Search, CalendarDays } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Section } from "@/components/site/Section";
import { SessionStartLine } from "@/components/site/UpcomingSessions";

import type { ScheduledSession } from "@/content/schedule";
import { useScheduledSessions } from "@/hooks/useScheduledSessions";
import { getCourse } from "@/content/courses";
import { SITE_URL } from "@/lib/seo";

export const Route = createFileRoute("/schedule")({
  head: () => ({
    links: [{ rel: "canonical", href: `${SITE_URL}/schedule/` }],
    meta: [
      { title: "View Entire Schedule | Leadership Edge Live" },
      {
        name: "description",
        content:
          "Every upcoming Leadership Edge Live session, month by month. Search by course and register for the dates that fit your team.",
      },
      { property: "og:title", content: "View Entire Schedule | Leadership Edge Live" },
      {
        property: "og:description",
        content: "Every upcoming live leadership session, month by month.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: SchedulePage,
});

/** Open on the month of the next upcoming session, or the current month. */
function initialMonth(list: ScheduledSession[]): Date {
  const today = new Date();
  const upcoming = list
    .map((s) => parseISO(s.startDate))
    .filter((d) => d >= today)
    .sort((a, b) => a.getTime() - b.getTime())[0];
  return startOfMonth(upcoming ?? today);
}

function titleFor(s: ScheduledSession) {
  return getCourse(s.courseSlug)?.title ?? s.courseSlug;
}

function SchedulePage() {
  const { upcoming, loading } = useScheduledSessions();
  const [month, setMonth] = useState<Date>(() => startOfMonth(new Date()));
  const [monthTouched, setMonthTouched] = useState(false);
  useEffect(() => {
    if (!monthTouched && upcoming.length) setMonth(initialMonth(upcoming));
  }, [upcoming, monthTouched]);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return upcoming.filter((s) => (q ? titleFor(s).toLowerCase().includes(q) : true));
  }, [query, upcoming]);

  const monthSessions = useMemo(
    () =>
      filtered
        .filter((s) => isSameMonth(parseISO(s.startDate), month))
        .sort((a, b) => a.startDate.localeCompare(b.startDate)),
    [filtered, month],
  );

  const searching = query.trim().length > 0;

  const listSessions = useMemo(
    () =>
      searching
        ? [...filtered].sort((a, b) => a.startDate.localeCompare(b.startDate))
        : monthSessions,
    [searching, filtered, monthSessions],
  );

  const days = useMemo(
    () =>
      eachDayOfInterval({
        start: startOfWeek(startOfMonth(month)),
        end: endOfWeek(endOfMonth(month)),
      }),
    [month],
  );

  return (
    <>
      <PageHero
        maxWidth="6xl"
        headline={<>Tons of amazing{"\u00a0"}courses</>}
        supporting="You can enroll for any of these sessions in our learning management system after you become a member. This is a view-only schedule."
        ctas={[
          { label: "Buy for yourself", to: "/grow/", variant: "primary" },
          { label: "Buy for your team", to: "/purchase/", variant: "secondary" },
        ]}
      />

      <Section>
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setMonthTouched(true);
                setMonth((m) => addMonths(m, -1));
              }}
              aria-label="Previous month"
              className="inline-flex h-10 w-10 items-center justify-center rounded-[6px] border border-border/60 text-ink transition-colors hover:bg-lavender"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <p className="min-w-44 text-center font-display text-xl text-ink">
              {format(month, "MMMM yyyy")}
            </p>
            <button
              type="button"
              onClick={() => {
                setMonthTouched(true);
                setMonth((m) => addMonths(m, 1));
              }}
              aria-label="Next month"
              className="inline-flex h-10 w-10 items-center justify-center rounded-[6px] border border-border/60 text-ink transition-colors hover:bg-lavender"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search courses"
                aria-label="Search sessions by course"
                className="w-full rounded-full border border-border/60 bg-background py-2.5 pl-10 pr-4 text-base text-ink sm:text-sm shadow-sm outline-none focus:border-primary/40 focus:ring-2 focus:ring-primary/15"
              />
            </div>
          </div>
        </div>

        {/* Desktop month grid */}
        <div className="hidden overflow-hidden rounded-2xl border border-border/60 bg-background md:block">
          <div className="grid grid-cols-7 border-b border-border/60 bg-lavender">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div
                key={d}
                className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wider text-primary"
              >
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {days.map((day) => {
              const inMonth = isSameMonth(day, month);
              const chips = monthSessions.filter((s) => isSameDay(parseISO(s.startDate), day));
              return (
                <div
                  key={day.toISOString()}
                  className={`min-h-28 border-b border-r border-border/40 p-2 ${inMonth ? "" : "bg-muted/30"}`}
                >
                  <p className={`mb-1 text-xs ${inMonth ? "text-ink" : "text-muted-foreground"}`}>
                    {format(day, "d")}
                  </p>
                  <div className="space-y-1">
                    {chips.map((s) => (
                      <Link
                        key={s.id}
                        to="/course-catalog/$slug/"
                        params={{ slug: s.courseSlug }}
                        className="block truncate rounded-md bg-lavender px-2 py-1 text-[0.7rem] font-medium text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
                        title={titleFor(s)}
                      >
                        {titleFor(s)}
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile agenda + full detail list */}
        <div className="mt-8">
          <h2 className="mb-4 font-display text-2xl text-ink">
            {searching
              ? `All sessions matching "${query.trim()}"`
              : `${format(month, "MMMM yyyy")} sessions`}
          </h2>
          {listSessions.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border/70 bg-background p-8 text-center">
              <p className="text-sm text-muted-foreground">
                {searching
                  ? "No sessions match that search. Tell us what you need and we'll schedule around you."
                  : "Nothing scheduled this month. Tell us when you need it and we'll schedule around you."}
              </p>
              <Link
                to="/contact/"
                className="mt-4 inline-flex items-center rounded-[6px] bg-[#693D94] px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-brand"
              >
                Request a date
              </Link>
            </div>
          ) : (
            <ul className="space-y-3">
              {listSessions.map((s) => {
                return (
                  <li
                    key={s.id}
                    className="flex flex-col gap-4 rounded-2xl border border-border/60 bg-background p-6 transition-all hover:-translate-y-1 hover:border-primary/30 hover:shadow-[0_10px_30px_-8px_rgba(0,0,0,0.15)] sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="flex items-start gap-3">
                      <CalendarDays
                        className="mt-0.5 h-5 w-5 shrink-0 text-primary"
                        aria-hidden="true"
                      />
                      <div>
                        <Link
                          to="/course-catalog/$slug/"
                          params={{ slug: s.courseSlug }}
                          className="font-display text-lg text-ink hover:text-primary"
                        >
                          {titleFor(s)}
                        </Link>
                        <p className="mt-1 text-sm text-muted-foreground">
                          <SessionStartLine session={s} />
                        </p>
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:items-center">
                      <Link
                        to="/start/"
                        search={{ plan: "one-course", session: s.id }}
                        className="inline-flex items-center justify-center rounded-[6px] bg-[#693D94] px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-brand"
                      >
                        Register
                      </Link>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </Section>
    </>
  );
}
